import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";

import Col from "antd/es/col";
import Radio, { RadioChangeEvent } from "antd/es/radio";
import Row from "antd/es/row";
import Typography from "antd/es/typography";
import { defineMessages, FormattedMessage, useIntl } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import { EntityType, EntityTypeSupportingReports } from "@mapmycustomers/shared/types/entity";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { LoadingSpinner } from "@mapmycustomers/ui";

import { StandardFilterButton } from "@app/component/filtering";
import SelectColumnsButton from "@app/component/SelectColumnsPane/SelectColumnsButton";
import { skipNonExportFields } from "@app/component/SelectColumnsPane/util/fieldFilter";
import OrganizationSetting from "@app/enum/OrganizationSetting";
import useHiddenColumnsFromEntityTypeAndRole from "@app/scene/reports/utils/useHiddenColumnsFromEntityTypeAndRole";
import {
  areCheckInFieldsVisible,
  getOrganizationSettings,
  isCurrentUserMember,
} from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import layout from "@app/styles/layout";
import FilterOperatorAction from "@app/types/FilterOperatorAction";
import { useExtendedAnalytics } from "@app/util/analytic/AnalyticsService";
import getFeaturePathByEntityType from "@app/util/analytic/getFeaturePathByEntityType";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";
import useAnalytics from "@app/util/contexts/useAnalytics";

import {
  changeReportDescription,
  changeReportName,
  changeReportType,
  fetchPreviewData,
  initializeCreateReport,
} from "../../store/actions";
import {
  getCurrentReport,
  getPreviewColumnDefs,
  getPreviewViewState,
  getReportTotalRowsCount,
  isLoading,
} from "../../store/selectors";

import styles from "./Common.module.scss";
import DateTimeWarning from "./DateTimeWarning";
import PreviewGrid from "./PreviewGrid";
import ReportActions from "./ReportActions";
import Toolbar from "./Toolbar";

const supportedEntityTypes = [
  EntityType.COMPANY,
  EntityType.PERSON,
  EntityType.DEAL,
  EntityType.ACTIVITY,
  EntityType.TRIP,
] as const;

const optionNameMessages = defineMessages<(typeof supportedEntityTypes)[number]>({
  [EntityType.ACTIVITY]: {
    id: "reports.exports.create.header.option.activityReport",
    defaultMessage: "activities",
    description: "Option name for activities on the Create Report page",
  },
  [EntityType.COMPANY]: {
    id: "reports.exports.create.header.option.companyReport",
    defaultMessage: "companies",
    description: "Option name for companies on the Create Report page",
  },
  [EntityType.DEAL]: {
    id: "reports.exports.create.header.option.dealReport",
    defaultMessage: "deal",
    description: "Option name for deals on the Create Report page",
  },
  [EntityType.PERSON]: {
    id: "reports.exports.create.header.option.peopleReport",
    defaultMessage: "people",
    description: "Option name for people on the Create Report page",
  },
  [EntityType.TRIP]: {
    id: "reports.exports.create.header.option.tripReport",
    defaultMessage: "drives",
    description: "Option name for drives on the Create Report page",
  },
});

type Props = ReturnType<typeof mapStateToProps> & typeof mapDispatchToProps;

const CreateReport: React.FC<Props> = ({
  areCheckInFieldsVisible,
  changeReportDescription,
  changeReportName,
  changeReportType,
  columnDefs,
  initializeCreateReport,
  isMember,
  loading,
  organizationSettings,
  previewFetcher,
  previewViewState,
  report,
  reportTotalRowCount,
}) => {
  const intl = useIntl();
  useEffect(() => {
    initializeCreateReport();
  }, [initializeCreateReport]);

  const analyticIssuer = useExtendedAnalytics("Create Report", useAnalytics());

  const handleEntityTypeChange = useCallback(
    (e: RadioChangeEvent) => {
      changeReportType(e.target.value as EntityTypeSupportingReports);
      analyticIssuer.clicked([`For ${getFeaturePathByEntityType(e.target.value).join()}`]);
    },
    [analyticIssuer, changeReportType]
  );

  const fieldFilter = useCallback(
    (field: IField) =>
      skipNonExportFields(field) &&
      (areCheckInFieldsVisible || !field.hasFeature(FieldFeature.CHECK_IN)),
    [areCheckInFieldsVisible]
  );

  //adding organization timezone to reports (backend sets timezone as Etc/GMT by default which is not an IANA name on frontend)
  useEffect(() => {
    if (report && organizationSettings) {
      report[OrganizationSetting.TIMEZONE] = organizationSettings.find(
        (keys) => keys.key === OrganizationSetting.TIMEZONE
      )?.value;
    }
  }, [report, organizationSettings]);

  const filterOperators: FilterOperatorAction = useCallback(
    (field, defaultOperators) =>
      report?.tableName === EntityType.DEAL && field.hasFeature(FieldFeature.FUNNEL_FIELD)
        ? defaultOperators.filter(({ operator }) => operator !== FilterOperator.EQUALS)
        : defaultOperators,
    [report?.tableName]
  );

  const hiddenFields = useHiddenColumnsFromEntityTypeAndRole(report?.tableName, isMember);

  return loading || !report ? (
    <LoadingSpinner global />
  ) : (
    <section className={styles.container}>
      <AnalyticsContext.Provider value={analyticIssuer}>
        <PreviewGrid
          className={styles.previewGrid}
          columnDefs={columnDefs}
          previewFetcher={previewFetcher}
          previewViewState={previewViewState}
          report={report}
          reportTotalRowCount={reportTotalRowCount}
          toolbar={
            <div className={styles.topContainer}>
              <section className={styles.header}>
                <div>
                  <Typography.Title className={styles.title} level={3}>
                    <FormattedMessage
                      id="reports.exports.create.header.title"
                      defaultMessage="Create export"
                      description="Header title on the Create Export page"
                    />
                  </Typography.Title>
                  <Radio.Group
                    buttonStyle="solid"
                    className={styles.typeRadio}
                    onChange={handleEntityTypeChange}
                    value={report.tableName}
                  >
                    {supportedEntityTypes.map((entityType) => (
                      <Radio.Button key={entityType} value={entityType}>
                        {intl.formatMessage(optionNameMessages[entityType])}
                      </Radio.Button>
                    ))}
                  </Radio.Group>
                </div>
              </section>
              <section className={styles.subHeader}>
                <Toolbar
                  onDescriptionChange={changeReportDescription}
                  onNameChange={changeReportName}
                  report={report}
                />
                <Row gutter={layout.spacerM}>
                  <Col>
                    <StandardFilterButton
                      context={`${report.tableName!}ExportPage`}
                      entityType={report.tableName!}
                      filterOperators={filterOperators}
                      hiddenFields={hiddenFields}
                      hideSavedFilters={report.tableName === EntityType.TRIP}
                    />
                  </Col>
                  <Col>
                    <SelectColumnsButton entityType={report.tableName!} fieldFilter={fieldFilter} />
                  </Col>
                  <Col>
                    <ReportActions className={styles.actions} />
                  </Col>
                </Row>
              </section>
              <DateTimeWarning entityType={report.tableName!} />
            </div>
          }
        />
      </AnalyticsContext.Provider>
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  areCheckInFieldsVisible: areCheckInFieldsVisible(state),
  columnDefs: getPreviewColumnDefs(state)(false),
  isMember: isCurrentUserMember(state),
  loading: isLoading(state),
  organizationSettings: getOrganizationSettings(state),
  previewViewState: getPreviewViewState(state),
  report: getCurrentReport(state),
  reportTotalRowCount: getReportTotalRowsCount(state),
});

const mapDispatchToProps = {
  changeReportDescription,
  changeReportName,
  changeReportType,
  initializeCreateReport,
  previewFetcher: fetchPreviewData.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(CreateReport);
